// The CRM on the web, as on the phone (D-163): customers, their follow-ups of every kind, notes and history, and the
// owner's numbers. Types for what the database's CRM calls return, and the pure helpers the pages share; tested.
import { toCsv } from "./csv";
import { fmtDate, fmtDue, fromWallTime, localDate, toWallTime } from "./format";
import type { Interest, Role } from "./types";

// ------------------------------------------------------------------ what things are

/** The five kinds of follow-up she chooses from, as on the phone. A demo counts with meetings. */
export type FollowUpKind = "call" | "visit" | "meeting" | "send" | "collect";
/** Every type a follow-up may have: the five, and the older ones the AI and earlier screens still write. */
export type FollowUpType =
  | "call"
  | "visit"
  | "meeting"
  | "send"
  | "collect"
  | "site_visit"
  | "send_info"
  | "whatsapp"
  | "email"
  | "demo";
export type Label = "hot" | "warm" | "cold";
export type StageKind = "open" | "won" | "lost";
export type CustomerStatus = "open" | "won" | "lost" | "not_a_lead";
export type VisitResult = "happened" | "no_show" | "rescheduled";
export type Bucket = "overdue" | "today" | "tomorrow" | "week" | "later";

export interface Ref {
  id: string;
  name: string;
}

export interface StageRef {
  id: string;
  name: string;
  kind: StageKind;
  key: string | null;
}

export const FOLLOW_UP_KINDS: { kind: FollowUpKind; label: string; plural: string }[] = [
  { kind: "call", label: "Call", plural: "Calls" },
  { kind: "visit", label: "Visit", plural: "Visits" },
  { kind: "meeting", label: "Meeting", plural: "Meetings" },
  { kind: "send", label: "Send documents", plural: "Send documents" },
  { kind: "collect", label: "Collect payment", plural: "Collect payment" },
];

export function followUpKind(type: FollowUpType | string): FollowUpKind {
  switch (type) {
    case "visit":
    case "site_visit":
      return "visit";
    case "meeting":
    case "demo":
      return "meeting";
    case "send":
    case "send_info":
    case "whatsapp":
    case "email":
      return "send";
    case "collect":
      return "collect";
    default:
      return "call";
  }
}

/** "Visit", "Send documents": a follow-up's type in her words, as the phone says it (a demo stays a demo). */
export function followUpLabel(type: FollowUpType | string): string {
  if (type === "demo") return "Demo";
  return FOLLOW_UP_KINDS.find((k) => k.kind === followUpKind(type))!.label;
}

/** Visits and meetings (and demos) are booked beside the call follow-up and end with whether they happened. */
export function isVisit(type: FollowUpType | string): boolean {
  const kind = followUpKind(type);
  return kind === "visit" || kind === "meeting";
}

export const VISIT_RESULTS: { value: VisitResult; label: string }[] = [
  { value: "happened", label: "It happened" },
  { value: "no_show", label: "They did not come" },
  { value: "rescheduled", label: "Moved to another day" },
];

export const LABELS: Label[] = ["hot", "warm", "cold"];
export const labelName: Record<Label, string> = { hot: "Hot", warm: "Warm", cold: "Cold" };

/** Why a customer was lost, as the phone offers them; kept in English as the phone keeps them. */
export const LOST_REASONS = [
  "Budget too low",
  "Bought elsewhere",
  "Location",
  "Stopped answering",
  "Only looking",
  "Other",
];

/** The details she keeps on a customer, as on the phone's Edit customer (D-087). */
export const DETAIL_FIELDS: { key: string; label: string; placeholder: string }[] = [
  { key: "requirement", label: "Looking for", placeholder: "2BHK near the station" },
  { key: "location", label: "Location", placeholder: "Andheri West" },
  { key: "budget", label: "Budget", placeholder: "80 lakh" },
  { key: "timeline", label: "By when", placeholder: "Before Diwali" },
];

// ------------------------------------------------------------------ the customer list

export interface CustomerQuery {
  q?: string;
  stage?: string;
  status?: "open" | "won" | "lost" | "closed" | "";
  label?: Label | "none" | "";
  ai?: Label | "";
  /** A member's id, "me" or "none" (nobody's). */
  owner?: string;
  /** A source's id, or "none". */
  source?: string;
  follow_up?: "overdue" | "today" | "upcoming" | "none" | "";
  contact?: "never" | "stale" | "week" | "";
  added?: "today" | "week" | "month" | "";
  sort?: "next" | "recent" | "newest" | "name" | "";
  page: number;
}

export const CUSTOMERS_PAGE = 50;

export interface CustomerRow {
  id: string;
  name: string;
  has_name: boolean;
  phone: string;
  label: Label | null;
  interest: Interest;
  stage: StageRef | null;
  status: CustomerStatus;
  owner: Ref | null;
  source: { id: string; name: string; type: string } | null;
  /** How the customer came in: call_log, import, manual or integration. */
  origin: string;
  campaign: string | null;
  context: string | null;
  city: string | null;
  next_follow_up: {
    id: string;
    type: FollowUpType;
    kind: FollowUpKind;
    due_at: string;
    due_has_time: boolean;
    overdue: boolean;
    member: Ref | null;
  } | null;
  open_follow_ups: number;
  last_contact_at: string | null;
  last_connected_at: string | null;
  calls: number;
  connected_calls: number;
  created_at: string;
  enquired_at: string | null;
  assigned_at: string | null;
  expected_value: number | null;
}

export interface CustomerList {
  total: number;
  rows: CustomerRow[];
  stages: (StageRef & { count: number })[];
  /** Owners, admins and managers only; the unassigned have a null id. */
  owners: { id: string | null; name: string | null; count: number }[] | null;
  sources: { id: string | null; name: string | null; count: number }[];
}

/** The filters as the database takes them: empty ones left out, the page as a limit and an offset. */
export function customerQueryJson(q: CustomerQuery): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (
    const key of ["q", "stage", "status", "label", "ai", "owner", "source", "follow_up", "contact", "added", "sort"]
  ) {
    const v = (q as unknown as Record<string, string | undefined>)[key];
    if (v && v.trim()) out[key] = v.trim();
  }
  out.limit = CUSTOMERS_PAGE;
  out.offset = Math.max(0, q.page) * CUSTOMERS_PAGE;
  return out;
}

const QUERY_KEYS = ["q", "stage", "status", "label", "ai", "owner", "source", "follow_up", "contact", "added", "sort"];

/** The list's filters from the page's address, so a filtered list can be shared and comes back on Back. */
export function customerQueryFromParams(p: URLSearchParams): CustomerQuery {
  const q: Record<string, string | number> = { page: Math.max(0, Number(p.get("page") ?? 0) || 0) };
  for (const key of QUERY_KEYS) {
    const v = p.get(key);
    if (v) q[key] = v;
  }
  return q as unknown as CustomerQuery;
}

export function customerQueryToParams(q: CustomerQuery): URLSearchParams {
  const p = new URLSearchParams();
  for (const key of QUERY_KEYS) {
    const v = (q as unknown as Record<string, string | undefined>)[key];
    if (v) p.set(key, v);
  }
  if (q.page > 0) p.set("page", String(q.page));
  return p;
}

/** Ready-made views of the list: one tap for what an owner or a telecaller looks for most. */
export const CUSTOMER_VIEWS: { key: string; label: string; query: Partial<CustomerQuery>; managers?: boolean }[] = [
  { key: "overdue", label: "Overdue", query: { follow_up: "overdue" } },
  { key: "today", label: "Due today", query: { follow_up: "today" } },
  { key: "never", label: "Never called", query: { contact: "never", status: "open" } },
  { key: "no_next", label: "No next step", query: { follow_up: "none", contact: "week" } },
  { key: "stale", label: "Quiet for a week", query: { contact: "stale", status: "open" } },
  { key: "hot", label: "Hot", query: { label: "hot", status: "open" } },
  { key: "unassigned", label: "Nobody's", query: { owner: "none" }, managers: true },
];

/** Which ready-made view the filters are, if any. */
export function viewOf(q: CustomerQuery): string | null {
  const set = Object.entries(customerQueryJson({ ...q, page: 0, sort: "" })).filter(([k]) =>
    k !== "limit" && k !== "offset"
  );
  for (const v of CUSTOMER_VIEWS) {
    const want = Object.entries(v.query);
    if (want.length === set.length && want.every(([k, val]) => set.some(([sk, sv]) => sk === k && sv === val))) {
      return v.key;
    }
  }
  return null;
}

// ------------------------------------------------------------------ one customer

/** A follow-up as the customer's page has it (the phone's own card). */
export interface CustomerFollowUp {
  id: string;
  type: FollowUpType;
  due_at: string;
  due_has_time: boolean;
  overdue: boolean;
  reason: string;
  brief: string | null;
  source: string;
  rule: string | null;
  place: string | null;
  /** The member it is assigned to. */
  assigned_to: string | null;
  booked_by: string | null;
}

export interface TimelineEvent {
  id: string;
  type: string;
  at: string;
  actor: "member" | "ai" | "rule" | "system";
  by: string | null;
  payload: Record<string, unknown>;
  call: {
    id?: string;
    outcome?: string | null;
    recording?: { id: string; status: string; duration_s: number | null; playable?: boolean; summary?: string | null };
  } | null;
}

export interface CustomerPage {
  customer: {
    id: string;
    name: string;
    has_name: boolean;
    phone: string;
    label: Label | null;
    score: Label | null;
    context: string | null;
    stage: string | null;
    stage_id: string | null;
    status: CustomerStatus;
    next_follow_up_at: string | null;
    last_contact_at: string | null;
    calls: number;
    connected_calls: number;
    last_connected_at: string | null;
    enquired_at: string | null;
    assigned_at: string | null;
    created_at: string;
    owner: Ref | null;
    source: string | null;
    details: Record<string, unknown> | null;
    expected_value: number | null;
    address: string | null;
    city: string | null;
    lost_reason: string | null;
    label_set_at: string | null;
    summary?: string | null;
  };
  numbers: {
    id: string;
    e164: string;
    label: string | null;
    is_primary: boolean;
    is_whatsapp: boolean;
    contact_id: string | null;
  }[];
  contacts: { id: string; name: string | null; role: string | null; is_primary: boolean }[];
  follow_ups: CustomerFollowUp[];
  timeline: TimelineEvent[];
  stages: StageRef[];
}

/** The people of a customer with their numbers, the main contact first, then numbers saved without a person. */
export function peopleOf(page: Pick<CustomerPage, "numbers" | "contacts">) {
  const people = page.contacts.map((c) => ({
    contact: c,
    numbers: page.numbers.filter((n) => n.contact_id === c.id),
  }));
  const loose = page.numbers.filter((n) => !n.contact_id || !page.contacts.some((c) => c.id === n.contact_id));
  return { people, loose };
}

/** The details the customer's page keeps (requirement, location, budget, by when), as text. */
export function detailsOf(details: Record<string, unknown> | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of DETAIL_FIELDS) {
    const v = details?.[f.key];
    if (typeof v === "string" && v.trim()) out[f.key] = v.trim();
  }
  return out;
}

/** What changed in the details: only the changed keys are sent, an emptied one as "" (which removes it). */
export function changedDetails(before: Record<string, string>, after: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of DETAIL_FIELDS) {
    const a = (before[f.key] ?? "").trim();
    const b = (after[f.key] ?? "").trim();
    if (a !== b) out[f.key] = b;
  }
  return out;
}

const OUTCOMES: Record<string, string> = {
  call_back: "Call back",
  interested: "Interested",
  visit_booked: "Visit booked",
  not_now: "Not now",
  not_interested: "Not interested",
  wrong_number: "Wrong number",
  not_connected: "Not connected",
};

export function outcomeName(outcome: string | null | undefined): string | null {
  return outcome ? OUTCOMES[outcome] ?? outcome.replace(/_/g, " ") : null;
}

/**
 * A history entry in words, as the phone tells them, or null to leave it out (score changes and the like show where
 * they matter). Calls are drawn by the page itself, with their recordings.
 */
export function eventWords(
  e: TimelineEvent,
  stageNames: Map<string, string>,
  names: Map<string, string>,
): string | null {
  const p = e.payload ?? {};
  const text = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : null);
  switch (e.type) {
    case "lead.created":
      return "New customer";
    case "lead.stage": {
      const name = stageNames.get(String(p.stage_id ?? "")) ?? text(p.to)?.replace(/_/g, " ");
      if (!name) return null;
      const reason = text(p.lost_reason);
      return `Stage: ${name}${reason ? ` · ${reason}` : ""}`;
    }
    case "lead.label": {
      const to = text(p.to);
      return to ? `Marked ${to.charAt(0).toUpperCase()}${to.slice(1)}` : "Label removed";
    }
    case "lead.owner": {
      const to = names.get(String(p.to ?? ""));
      return to ? `Given to ${to}` : p.to ? "Given to someone" : "Left with nobody";
    }
    case "followup.created":
      return `${followUpLabel(String(p.type ?? "call"))} planned`;
    case "followup.done":
      return `${followUpLabel(String(p.type ?? "call"))} done`;
    case "followup.cancelled":
      return `${followUpLabel(String(p.type ?? "call"))} cancelled`;
    case "followup.result":
      return { happened: "Visit happened", no_show: "Visit: no-show", rescheduled: "Visit moved" }[
        String(p.result)
      ] ?? null;
    case "followup.reassigned": {
      const to = names.get(String(p.to ?? ""));
      return to ? `Follow-up given to ${to}` : null;
    }
    case "lead.enquired": {
      const what = [text(p.source), text(p.ad)].filter(Boolean).join(" · ");
      return what ? `Enquired · ${what}` : "Enquired";
    }
    case "note.logged":
      return text(p.text) ? `Note: ${text(p.text)}` : "Note";
    case "interaction.note":
      return "Voice note";
    case "whatsapp.logged":
      return "WhatsApp message";
    default:
      return null;
  }
}

// ------------------------------------------------------------------ follow-ups of a person or the team

export interface FollowUpQuery {
  /** "me", "all" (owners, admins and managers) or a member's id. */
  who?: string;
  kind?: FollowUpKind | "";
  due?: Bucket | "";
  state?: "open" | "done";
  q?: string;
  page: number;
}

export const FOLLOW_UPS_PAGE = 100;

export interface FollowUpRow {
  id: string;
  type: FollowUpType;
  kind: FollowUpKind;
  status: "open" | "done" | "cancelled" | "missed";
  due_at: string;
  due_has_time: boolean;
  bucket: Bucket | null;
  overdue: boolean;
  reason: string;
  brief: string | null;
  source: string;
  rule: string | null;
  place: string | null;
  result: VisitResult | null;
  completed_at: string | null;
  snoozed_count: number;
  created_at: string;
  member: Ref | null;
  booked_by: Ref | null;
  created_by: Ref | null;
  lead: {
    id: string;
    name: string;
    phone: string;
    label: Label | null;
    interest: Interest;
    status: CustomerStatus;
    context: string | null;
    stage: string | null;
    owner: Ref | null;
  };
}

export interface FollowUpPerson {
  id: string;
  name: string;
  role: Role;
  overdue: number;
  today: number;
  upcoming: number;
  visits: number;
  done_today: number;
  done_week: number;
  on_time_week: number;
}

export interface FollowUpList {
  total: number;
  rows: FollowUpRow[];
  buckets: Record<Bucket, number>;
  kinds: Record<FollowUpKind, number>;
  people: FollowUpPerson[];
}

export function followUpQueryJson(q: FollowUpQuery): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (q.who) out.who = q.who;
  if (q.kind) out.kind = q.kind;
  if (q.due) out.due = q.due;
  if (q.state) out.state = q.state;
  if (q.q?.trim()) out.q = q.q.trim();
  out.limit = FOLLOW_UPS_PAGE;
  out.offset = Math.max(0, q.page) * FOLLOW_UPS_PAGE;
  return out;
}

export const BUCKETS: { bucket: Bucket; label: string }[] = [
  { bucket: "overdue", label: "Overdue" },
  { bucket: "today", label: "Today" },
  { bucket: "tomorrow", label: "Tomorrow" },
  { bucket: "week", label: "This week" },
  { bucket: "later", label: "Later" },
];

/** What the follow-up form saves: a new follow-up (with the customer) or a change to one (with its id). */
export interface FollowUpInput {
  id?: string;
  lead_id?: string;
  type: FollowUpKind;
  due_at: string;
  due_has_time: boolean;
  assigned_to?: string | null;
  place?: string | null;
  reason?: string | null;
}

/** On time as the dashboard counts it: done within 15 minutes of when it was due (DASH-02). */
export function onTimeShare(done: number, onTime: number): number | null {
  return done > 0 ? Math.round((onTime / done) * 100) : null;
}

// ------------------------------------------------------------------ the owner's numbers

export interface Insights {
  from: string;
  to: string;
  pipeline: (StageRef & { count: number; hot: number; warm: number; value: number | null })[];
  /** Open customers nobody has; owners, admins and managers only. */
  unassigned: number | null;
  moved: {
    new: number;
    contacted: number;
    visits_booked: number;
    visits_done: number;
    won: number;
    won_value: number | null;
    lost: number;
  };
  lost_reasons: { reason: string; count: number }[];
  sources: {
    id: string | null;
    name: string;
    leads: number;
    called: number;
    connected: number;
    interested: number;
    visit: number;
    won: number;
  }[];
  speed: { leads: number; called: number; within_5m: number; within_1h: number; median_minutes: number | null };
  people: {
    id: string;
    name: string;
    role: Role;
    open: number;
    untouched: number;
    no_next: number;
    overdue: number;
    done: number;
    on_time: number;
    visits_booked: number;
    won: number;
  }[];
}

/** "12%", or "–" when there is nothing to divide. */
export function pct(part: number, whole: number): string {
  return whole > 0 ? `${Math.round((part / whole) * 100)}%` : "–";
}

/** "45 min", "6.4 h", "2 days": how long enquiries waited for their first call. */
export function waitWords(minutes: number | null | undefined): string {
  if (minutes == null) return "–";
  if (minutes < 60) return `${Math.round(minutes)} min`;
  if (minutes < 48 * 60) return `${(Math.round((minutes / 60) * 10) / 10).toString()} h`;
  return `${Math.round(minutes / (24 * 60))} days`;
}

/** One of the company's WhatsApp messages (C7), named and written by its owners and admins (D-187). */
export interface MessageTemplate {
  id: string;
  name: string;
  /** The text per app language: {"en": "...", "hi": "..."}. */
  body: Record<string, string>;
  link: string | null;
  /** Offered after a visit is booked. */
  for_visits: boolean;
}

/** A message as Settings saves it; no id: a new one, offered last. */
export interface MessageTemplateInput {
  id?: string | null;
  name: string;
  body: Record<string, string>;
  link: string | null;
  for_visits: boolean;
}

/** What a message can say besides the names: a visit's day and time in words, where, the map link and who meets them,
 * and the company's name. */
export interface MessageDetails {
  day?: string | null;
  time?: string | null;
  place?: string | null;
  map?: string | null;
  handler?: string | null;
  company?: string | null;
}

/** What a message can carry, as Settings lists them. */
export const MESSAGE_PLACEHOLDERS: [string, string][] = [
  ["{name}", "the customer's first name"],
  ["{caller}", "the first name of whoever sends it"],
  ["{company}", "the company's name"],
  ["{day}", "a visit's day, such as Friday 24 Oct"],
  ["{time}", "its time, such as 4:00 pm"],
  ["{place}", "where it happens"],
  ["{map}", "the map link"],
  ["{handler}", "who will meet them"],
];

/** The first word of a name, or "" for none or a number. */
function firstWord(name: string | null | undefined): string {
  const n = (name ?? "").trim();
  return n === "" || n.startsWith("+") || /^\d/.test(n) ? "" : n.split(/\s+/)[0];
}

/**
 * A message with its details filled in, as the phone fills it (app/lib/core/messages.dart): {name} and {caller} are
 * first names, left blank when unknown, so a greeting goes on without one; a line holding any other detail that is
 * missing is left out, so a message sent without a visit, or a company with no map link, sends no empty line for it.
 */
export function fillMessage(
  body: string,
  customer: string | null | undefined,
  caller: string | null | undefined,
  details: MessageDetails = {},
): string {
  const clean = (v: string | null | undefined) => (v ?? "").trim();
  const vals: Record<string, string> = {
    "{name}": firstWord(customer),
    "{caller}": firstWord(caller),
    "{day}": clean(details.day),
    "{time}": clean(details.time),
    "{place}": clean(details.place),
    "{map}": clean(details.map),
    "{handler}": firstWord(details.handler),
    "{company}": clean(details.company),
  };
  const blankable = new Set(["{name}", "{caller}"]);
  return body.trim().split("\n").flatMap((line) => {
    const used = Object.keys(vals).filter((k) => line.includes(k));
    if (used.some((k) => vals[k] === "" && !blankable.has(k))) return [];
    let out = line;
    for (const k of used) out = out.split(k).join(vals[k]);
    out = out.replace(/[ \t]+,/g, ",").replace(/[ \t]{2,}/g, " ").trim();
    // A line that was only a name left blank ("– {caller}" with none) goes too.
    return used.length && !/[\p{L}\p{N}]/u.test(out) ? [] : [out];
  }).join("\n").trim();
}

/** A message's text in a language, else English, else whichever it has. */
export function messageText(t: Pick<MessageTemplate, "body">, language: string | null | undefined): string {
  return t.body[language ?? "en"] ?? t.body.en ?? Object.values(t.body)[0] ?? "";
}

/** A company message filled in for one customer, its link last. */
export function filledMessage(
  t: MessageTemplate,
  language: string | null | undefined,
  customer: string | null | undefined,
  caller: string | null | undefined,
  details: MessageDetails = {},
): string {
  return [fillMessage(messageText(t, language), customer, caller, details), t.link ?? ""].filter(Boolean).join("\n");
}

/** What is wrong with a message before it is saved, in the database's words, or null. */
export function messageProblem(t: MessageTemplateInput): string | null {
  const name = t.name.trim();
  if (name.length < 1 || name.length > 60) return "Name the message, in up to 60 characters.";
  const texts = Object.values(t.body).map((b) => b.trim()).filter(Boolean);
  if (texts.length === 0) return "Write the message.";
  if (texts.some((b) => b.length > 1000)) return "Keep a message under 1,000 characters.";
  const link = (t.link ?? "").trim();
  if (link && (link.length > 500 || !/^https:\/\/\S+$/.test(link))) {
    return "A link is a web address starting with https://.";
  }
  return null;
}

/** The wa.me address that opens WhatsApp on the customer's number, with the message ready to send. */
export function whatsappLink(e164: string, text: string | null): string {
  const digits = e164.replace(/\D/g, "");
  return text ? `https://wa.me/${digits}?text=${encodeURIComponent(text)}` : `https://wa.me/${digits}`;
}

// ------------------------------------------------------------------ when

function addDays(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * When a follow-up is due, in words: as fmtDue for one at a time ("Overdue 2 h", "Today 15:00"); for one at any time
 * of a day, the day alone ("Today", "Tomorrow", "Fri 16 Oct"), overdue once that day has passed, as the phone counts it.
 */
export function dueWords(dueAt: string, hasTime: boolean, tz: string, now: Date = new Date()): string {
  if (hasTime) return fmtDue(dueAt, tz, now);
  const today = localDate(now, tz);
  const day = localDate(new Date(dueAt), tz);
  if (day < today) {
    const days = Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${day}T00:00:00Z`)) / 86_400_000);
    return days === 1 ? "Overdue since yesterday" : `Overdue ${days} days`;
  }
  if (day === today) return "Today, any time";
  if (day === addDays(today, 1)) return "Tomorrow, any time";
  return `${fmtDate(dueAt, tz)}, any time`;
}

/** Overdue as the phone counts it: one at a time once its time has passed, one for any time once its day has. */
export function isDueOver(dueAt: string, hasTime: boolean, tz: string, now: Date = new Date()): boolean {
  if (hasTime) return Date.parse(dueAt) < now.getTime();
  return localDate(new Date(dueAt), tz) < localDate(now, tz);
}

/** The days the follow-up form offers: today, tomorrow and the next few, as "YYYY-MM-DD" with a label. */
export function dayChoices(now: Date, tz: string, count = 6): { day: string; label: string }[] {
  const today = localDate(now, tz);
  return Array.from({ length: count }, (_, i) => {
    const day = addDays(today, i);
    const label = i === 0
      ? "Today"
      : i === 1
      ? "Tomorrow"
      : new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", weekday: "short", day: "numeric", month: "short" })
        .format(new Date(`${day}T00:00:00Z`));
    return { day, label };
  });
}

export const TIME_CHOICES = ["10:00", "11:00", "13:00", "16:00", "18:00"];

/** The moment a follow-up is due: the day at the chosen time, or (any time) at 10:00 as the phone keeps it. */
export function dueAt(day: string, time: string | null, tz: string): { due_at: string; due_has_time: boolean } {
  return {
    due_at: fromWallTime(`${day}T${time ?? "10:00"}`, tz).toISOString(),
    due_has_time: time !== null,
  };
}

/** Where the snooze choices take a follow-up: an hour on, tomorrow at 11:00, or a week on at the same time. */
export function snoozeChoices(now: Date, tz: string): { key: string; label: string; until: string }[] {
  const today = localDate(now, tz);
  return [
    { key: "hour", label: "1 hour", until: new Date(now.getTime() + 3600_000).toISOString() },
    { key: "tomorrow", label: "Tomorrow 11:00", until: fromWallTime(`${addDays(today, 1)}T11:00`, tz).toISOString() },
    { key: "week", label: "In a week", until: new Date(now.getTime() + 7 * 86_400_000).toISOString() },
  ];
}

// ------------------------------------------------------------------ export

/** The most customers one download holds: 40 pages of the list. */
export const EXPORT_LIMIT = 2000;

/**
 * A cell a spreadsheet will not run as a formula. Names and answers come from ad forms anyone can fill in, so text
 * starting with = + - @ (or a tab or return) is kept as text with a leading apostrophe; a phone number stays as it is.
 */
export function safeCell(value: string): string {
  return /^[=+\-@\t\r]/.test(value) && !/^\+?\d[\d ]*$/.test(value) ? `'${value}` : value;
}

/** The customers as a spreadsheet: one row each, times as "2026-10-11 14:30" in the company's time zone. */
export function customersCsv(rows: CustomerRow[], tz: string): string {
  const when = (iso: string | null) => (iso ? toWallTime(iso, tz).replace("T", " ") : "");
  const origin = (r: CustomerRow) =>
    r.source?.name ??
      ({ call_log: "Calls", import: "Imported", manual: "Added by hand", integration: "Integration" }[r.origin] ??
        r.origin);
  return toCsv([
    [
      "Name",
      "Phone",
      "Stage",
      "Status",
      "Label",
      "Owner",
      "Source",
      "Campaign",
      "Details",
      "Next follow-up",
      "Due",
      "Last contact",
      "Calls",
      "Connected",
      "Added",
      "Enquired",
    ],
    ...rows.map((r) =>
      [
        r.has_name ? r.name : "",
        r.phone,
        r.stage?.name ?? "",
        r.status,
        r.label ? labelName[r.label] : "",
        r.owner?.name ?? "",
        origin(r),
        r.campaign ?? "",
        r.context ?? "",
        r.next_follow_up ? followUpLabel(r.next_follow_up.type) : "",
        r.next_follow_up
          ? (r.next_follow_up.due_has_time ? when(r.next_follow_up.due_at) : when(r.next_follow_up.due_at).slice(0, 10))
          : "",
        when(r.last_contact_at),
        String(r.calls),
        String(r.connected_calls),
        when(r.created_at),
        when(r.enquired_at),
      ].map(safeCell)
    ),
  ]);
}
